import { useTranslation } from 'react-i18next';
import { UIButton, UIModal, UIModalProps, UINumberInput, UITypography } from 'shared/ui';
import styles from './style.module.scss';
import { useRef, useCallback, useState, useMemo } from 'react';
import { useGlobalStore } from 'shared/store';
import { formatLargeNumber, formatTonAddress, getTonAddressUserFriendly } from 'shared/utils';
import { useWalletConnect } from 'shared/hooks';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'shared/constants';
import UsdtBanner from 'assets/images/usdt-banner.png';
import TonIcon from 'assets/icons/ton.png';
import WalletRoundIcon from 'assets/icons/wallet-round-icon.svg?react';

type WithdrawAmountModalProps = UIModalProps & {
  onComplete: (amount: number, walletAddress: string) => void;
  value: string;
  onChange: (value: string) => void;
};

const MIN_WITHDRAWAL_AMOUNT = 10;

const createAmountValidator = (maxAmount: number) => {
  return (inputValue: string) => {
    const cleanValue = inputValue.replace(/\s+/g, '');
    const numValue = Number(cleanValue);

    return (
      cleanValue && !isNaN(numValue) && numValue >= MIN_WITHDRAWAL_AMOUNT && numValue <= maxAmount
    );
  };
};

export const WithdrawAmountModal = ({
  onComplete,
  value,
  onChange,
  ...props
}: WithdrawAmountModalProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // CRITICAL: This component MUST always be rendered in AppHeader!
  // It contains the global wallet status listener that syncs with backend.
  // Other components (like MarketScreen) rely on this listener being active.
  const { wallet, handleWalletAction } = useWalletConnect();
  
  const modalRef = useRef<HTMLDivElement | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const user = useGlobalStore((state) => state.user);


  const maxAmount = useMemo(() => {
    // Truncate to 2 decimal places
    return Math.floor(Number(user.usdt) * 100) / 100;
  }, [user.usdt]);

  const onFocus = useCallback(() => {
    if (!modalRef.current) return;
    modalRef.current.style.top = `0px`;
  }, []);

  const onBlur = useCallback(() => {
    if (!modalRef.current) return;
    modalRef.current.style.top = `auto`;
  }, []);

  const handleComplete = async () => {
    const amount = Number(value);
    if (!isValidValue(value)) return;

    // Button should be disabled if no wallet, but just in case
    if (!wallet) return;

    setIsLoading(true);
    try {
      // Get user-friendly formatted address for backend
      const userFriendlyAddress = getTonAddressUserFriendly(
        wallet.account.address,
        wallet.account.chain,
      );
      await onComplete(amount, userFriendlyAddress);
    } catch (error) {
      console.error('Error occurred during withdrawal:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUseMaxBalance = () => {
    onChange(maxAmount.toString());

    const event = new Event('input', { bubbles: true });
    const inputElement = modalRef.current?.querySelector('input');
    if (inputElement) {
      inputElement.value = maxAmount.toString();
      inputElement.dispatchEvent(event);
    }
  };

  const isValidValue = useCallback(createAmountValidator(maxAmount), [maxAmount]);

  // Validate on change to prevent invalid input
  const handleChange = (newValue: string) => {
    const cleanValue = newValue.replace(/\s+/g, '');
    const numValue = Number(cleanValue);

    // Allow empty string or valid numbers
    if (cleanValue === '' || (!isNaN(numValue) && numValue >= 0)) {
      // Limit to 2 decimal places
      if (cleanValue.includes('.')) {
        const parts = cleanValue.split('.');
        if (parts[1] && parts[1].length > 2) {
          return;
        }
      }

      // Prevent entering more than max amount
      if (numValue > maxAmount) {
        onChange(maxAmount.toString());
      } else {
        onChange(newValue);
      }
    }
  };

  return (
    <UIModal visible={props.visible} onClose={props.onClose} zIndex={999} detent="content-height">
      <div className={styles.root} ref={modalRef}>
        {/* Header with balance */}
        <div className={styles.header}>
          <img src={UsdtBanner} alt="USDT" className={styles.usdtIcon} />
          <div className={styles.balanceInfo}>
            <UITypography className={styles.balanceLabel}>
              {t('v2.withdrawal.balance')}
            </UITypography>
            <UITypography className={styles.balanceAmount}>
              {formatLargeNumber(maxAmount, 2).replace(/\s/g, ' ')} USDT
            </UITypography>
          </div>
          <button 
            onClick={() => {
              props.onClose?.();
              navigate(ROUTES.BAKERY, { state: { activeWindow: 'withdrawals' } });
            }} 
            className={styles.historyButton}
          >
            {t('v2.withdrawal.history.title')}
          </button>
        </div>

        {/* Amount input section */}
        <div className={styles.amountSection}>
          <div className={styles.amountControls}>
            <div className={styles.minLabel}>min: {MIN_WITHDRAWAL_AMOUNT} USDT</div>
            <button onClick={handleUseMaxBalance} className={styles.maxButton}>
              max
            </button>
          </div>

          <div className={styles.inputContainer}>
            <UINumberInput
              value={value}
              onChange={handleChange}
              variant="secondary"
              onFocus={onFocus}
              onBlur={onBlur}
              className={styles.amountInput}
              decimals={2}
              placeholder="0"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && isValidValue(value) && wallet) {
                  handleComplete();
                }
              }}
            />
            <span className={styles.usdtLabel}>USDT</span>
          </div>
        </div>

        {/* Wallet section */}
        <div className={styles.walletSection}>
          {wallet ? (
            <button className={styles.walletAddress} onClick={handleWalletAction}>
              <WalletRoundIcon className={styles.walletIcon} />
              <span>{formatTonAddress(wallet.account.address, wallet.account.chain)}</span>
              <div className={styles.tonBadge}>
                <img src={TonIcon} alt="TON" className={styles.tonIconSmall} />
                <span>TON</span>
              </div>
            </button>
          ) : (
            <button className={styles.walletAddress} onClick={handleWalletAction}>
              <WalletRoundIcon className={styles.walletIcon} />
              <span>{t('settings.connect_wallet_modal.connect_wallet')}</span>
            </button>
          )}
        </div>

        {/* Error message */}
        {value && !isValidValue(value) && (
          <UITypography className={styles.errorText}>
            {Number(value) < MIN_WITHDRAWAL_AMOUNT
              ? t('v2.withdrawal.amount_validation_min', { min: MIN_WITHDRAWAL_AMOUNT })
              : t('v2.withdrawal.amount_validation_max')}
          </UITypography>
        )}

        {/* Submit button */}
        <UIButton
          onClick={handleComplete}
          fullwidth
          className={styles.submitButton}
          disabled={isLoading || !value || !isValidValue(value) || !wallet}
          loading={isLoading}
        >
          {!wallet
            ? t('settings.connect_wallet_modal.connect_wallet')
            : isLoading
              ? t('v2.withdrawal.processing')
              : t('v2.withdrawal.withdraw')}
        </UIButton>
      </div>
    </UIModal>
  );
};
